import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
    Box,
    Container,
    FormControl,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import EChartsReact from "echarts-for-react";

import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import {
    COMPARISON_SAME_TIME_LAST_YEAR,
    DATATYPE_CHECKIN,
    DATATYPE_SEARCHES,
    DEFAULT_COMPARISON,
    GROUP_DATE_DAY,
    GROUP_DATE_MONTH,
    GROUP_DATE_WEEK,
    PRESET_CHOOSE_MONTH,
    PRESET_LAST_12_MONTHS,
    PRESET_LAST_30_DAYS,
    PRESET_LAST_3_MONTHS,
    PRESET_LAST_6_MONTHS,
    PRESET_LAST_7_DAYS,
    PRESET_LAST_FULL_MONTH,
    PRESET_LAST_YEAR,
    PRESET_MONTH_TO_DATE,
    PRESET_NEXT_3_MONTHS,
    PRESET_NEXT_MONTH,
    PRESET_TOMORROW,
    PRESET_YEAR_TO_DATE,
    PRESET_YESTERDAY,
} from "../helpers/filters";
import Colours from "../helpers/colours";
import { fns } from "../helpers/common";
import { startOfWeekDate } from "../helpers/dates";

import FiltersReport, { ITEM_HEIGHT } from "../components/FiltersReport";

import DataLoader from "../components/DataLoader";
import NoDataRow from "../components/NoDataRow";
import HoverPaper from "../components/shared/HoverPaper";
import InfoIcon from "../components/InfoIcon";
import { TARGET_OFF } from "../helpers/targets";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const formatDateByGroup = (dateString, breakdownMode) => {
    let result = dayjs(dateString);
    switch (breakdownMode) {
        case GROUP_DATE_DAY:
            result = result.format("MMM D");
            break;
        case GROUP_DATE_WEEK:
            result = result.format("MMM D");
            break;
        case GROUP_DATE_MONTH:
            result = result.format("MMM");
            break;
        default:
            result = result.format("MMM D");
    }
    return result;
};

const DEFAULT_BREAKDOWN_MODE = GROUP_DATE_WEEK;

const BreakdownBySelector = ({ breakdownMode, setBreakdownMode }) => {
    return (
        <Stack direction={"column"}>
            <Typography>
                Breakdown by
                {/* Daily / Weekly / Monthly */}
            </Typography>
            <FormControl>
                <Select
                    labelId="breakdownFilter"
                    id="breakdownFilter"
                    value={breakdownMode}
                    sx={{
                        height: ITEM_HEIGHT,
                        width: "140px",
                    }}
                    onChange={(event) => {
                        let value = event.target.value;
                        setBreakdownMode(value);
                    }}
                    size="small"
                >
                    <MenuItem value={GROUP_DATE_MONTH}>Monthly</MenuItem>
                    <MenuItem value={GROUP_DATE_WEEK}>Weekly</MenuItem>
                    <MenuItem value={GROUP_DATE_DAY}>Daily</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );
};

const SessionsTrendReport = ({ data, hotelID }) => {
    const [breakdownMode, setBreakdownMode] = useState(DEFAULT_BREAKDOWN_MODE);

    return (
        <Stack direction={"column"} sx={{ pt: 2 }}>
            <Stack direction={"row"} alignItems="center" flex={1}>
                <Typography fontWeight={"bold"}>
                    Booking engine sessions by search and arrival period
                </Typography>
                <InfoIcon
                    open={false}
                    text={
                        "Displays booking engine sessions from users who searched during the selected search period for stays within the selected arrival period."
                    }
                />
            </Stack>
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{
                    p: 2,
                    backgroundColor: Colours.notificationCard,
                }}
            >
                {((data[breakdownMode] ?? {}).data ?? []).length > 0 ? (
                    <>
                        <Stack direction="column" spacing={2}>
                            <BreakdownBySelector
                                breakdownMode={breakdownMode}
                                setBreakdownMode={setBreakdownMode}
                            />
                            <Typography>
                                Average{" "}
                                {`${
                                    breakdownMode === GROUP_DATE_DAY
                                        ? "daily"
                                        : breakdownMode === GROUP_DATE_WEEK
                                          ? "weekly"
                                          : "monthly"
                                }`}{" "}
                                sessions:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {fns(
                                        hotelID,
                                        data[breakdownMode].average ?? 0,
                                    )}
                                </span>
                            </Typography>
                        </Stack>
                        <EChartsReact
                            // key={update}
                            option={{
                                legend: { show: false },
                                tooltip: {
                                    trigger: "axis",
                                },
                                xAxis: {
                                    boundaryGap: false,
                                    type: "category",
                                    data: data[breakdownMode].data.map(
                                        (date) => date.date,
                                    ),
                                    axisPointer: {
                                        label: {
                                            formatter: (params) =>
                                                dayjs(params.value).format(
                                                    "MMM D",
                                                ),
                                        },
                                    },

                                    axisLabel: {
                                        formatter: function (value) {
                                            return formatDateByGroup(
                                                value,
                                                breakdownMode,
                                            );
                                        },
                                    },
                                },
                                yAxis: {
                                    show: false,
                                },

                                series: [
                                    {
                                        type: "line",
                                        symbol: "none",
                                        smooth: true,
                                        color: "black",
                                        stack: "current",
                                        areaStyle: {
                                            opacity: 0.5,
                                        },

                                        data: data[breakdownMode].data.map(
                                            (date, index) => {
                                                return {
                                                    name: date.date,
                                                    value: date.sessions,
                                                    // symbolSize: 7,
                                                    // symbol:
                                                    //     dataSessionsTrend[
                                                    //         breakdownMode
                                                    //     ].data.length > 20
                                                    //         ? index % 6 ===
                                                    //           0
                                                    //             ? "circle"
                                                    //             : "none"
                                                    //         : "circle",
                                                };
                                            },
                                        ),
                                        markLine: {
                                            symbol: "none",
                                            lineStyle: {
                                                type: "solid",
                                                color: "gray",
                                            },
                                            data: [
                                                {
                                                    yAxis:
                                                        data[breakdownMode]
                                                            .average ?? 0,
                                                    name: "Average Daily Sessions",
                                                    label: {
                                                        fontSize: 15,
                                                        fontStyle: "bold",
                                                        position: "start",
                                                        formatter: (e) =>
                                                            `${fns(
                                                                hotelID,
                                                                data[
                                                                    breakdownMode
                                                                ].average ?? 0,
                                                            )}`,
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                ],
                            }}
                        />
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No session activity found for the selected period." />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </Stack>
    );
};

const SearchesForUnavailableDaysReport = ({ data, hotelID }) => {
    const [breakdownMode, setBreakdownMode] = useState(DEFAULT_BREAKDOWN_MODE);

    return (
        <>
            <Stack direction={"column"} sx={{ pt: 2 }}>
                <Stack direction={"row"} alignItems="center" flex={1}>
                    <Typography fontWeight={"bold"}>
                        Arrival dates with no available rooms
                    </Typography>
                    <InfoIcon
                        open={false}
                        text={
                            "Shows how many users found no availability for each arrival date during the selected search period."
                        }
                    />
                </Stack>

                <Container
                    maxWidth={false}
                    component={HoverPaper}
                    sx={{
                        p: 2,
                        backgroundColor: Colours.notificationCard,
                    }}
                >
                    {((data[breakdownMode] ?? {}).data ?? []).length > 0 ? (
                        <>
                            <Stack direction="column" spacing={2}>
                                <BreakdownBySelector
                                    breakdownMode={breakdownMode}
                                    setBreakdownMode={setBreakdownMode}
                                />
                                <Typography>
                                    Total searches:{" "}
                                    <span style={{ fontWeight: "bold" }}>
                                        {fns(
                                            hotelID,
                                            data[breakdownMode].total ?? 0,
                                        )}
                                    </span>
                                </Typography>
                            </Stack>
                            <EChartsReact
                                // key={update}
                                option={{
                                    legend: { show: false },
                                    tooltip: {
                                        trigger: "axis",
                                    },
                                    xAxis: {
                                        type: "category",
                                        data: data[breakdownMode].data.map(
                                            (date) => date.date,
                                        ),
                                        axisPointer: {
                                            label: {
                                                formatter: (params) =>
                                                    dayjs(params.value).format(
                                                        "MMM D",
                                                    ),
                                            },
                                        },
                                        axisLabel: {
                                            textStyle: {
                                                fontWeight: "light",
                                            },
                                            formatter: function (value) {
                                                return formatDateByGroup(
                                                    value,
                                                    breakdownMode,
                                                );
                                            },
                                        },
                                    },
                                    yAxis: {
                                        show: true,
                                    },

                                    series: [
                                        {
                                            type: "bar",
                                            symbol: "none",
                                            smooth: true,
                                            color: "#F08080",
                                            // label: {
                                            //     show: true,
                                            //     position: "top",
                                            //     color: "black",
                                            //     fontWeight: "bold",
                                            // },
                                            data: data[breakdownMode].data.map(
                                                (date, index) => {
                                                    return {
                                                        name: date.date,
                                                        value: date.searches,
                                                    };
                                                },
                                            ),
                                        },
                                    ],
                                }}
                            />
                        </>
                    ) : (
                        <Table>
                            <TableBody>
                                <NoDataRow text="No unavailable search results found for the selected criteria." />
                            </TableBody>
                        </Table>
                    )}
                </Container>
            </Stack>
        </>
    );
};

const AvailabilityVsNoAvailabilityReport = ({ data, hotelID }) => {
    const [breakdownMode, setBreakdownMode] = useState(DEFAULT_BREAKDOWN_MODE);

    return (
        <Stack direction={"column"} sx={{ pt: 2 }}>
            <Stack direction={"row"} alignItems="center" flex={1}>
                <Typography fontWeight={"bold"}>
                    Total bookings vs no availability
                </Typography>
                {/* <InfoIcon /> */}
            </Stack>
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                {((data[breakdownMode] ?? {}).data ?? []).length > 0 ? (
                    <>
                        <Stack direction="column" spacing={2}>
                            <BreakdownBySelector
                                breakdownMode={breakdownMode}
                                setBreakdownMode={setBreakdownMode}
                            />
                            <Typography>
                                Total Guests:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {fns(
                                        hotelID,
                                        data[breakdownMode].total ?? 0,
                                    )}
                                </span>
                            </Typography>
                        </Stack>
                        <EChartsReact
                            // key={update}
                            option={{
                                legend: {
                                    orient: "horizontal",
                                    width: "100%",
                                    bottom: 0,
                                },
                                tooltip: {
                                    trigger: "axis",
                                },
                                xAxis: {
                                    type: "category",
                                    data: data[breakdownMode].data.map(
                                        (date) => date.date,
                                    ),
                                    axisPointer: {
                                        label: {
                                            formatter: (params) =>
                                                dayjs(params.value).format(
                                                    "MMM D",
                                                ),
                                        },
                                    },
                                    axisLabel: {
                                        textStyle: {
                                            fontWeight: "light",
                                        },
                                        formatter: function (value) {
                                            return formatDateByGroup(
                                                value,
                                                breakdownMode,
                                            );
                                        },
                                    },
                                },
                                yAxis: {
                                    show: true,
                                },

                                series: [
                                    {
                                        type: "bar",
                                        name: "No Availability",
                                        stack: "current",
                                        symbol: "none",
                                        smooth: true,
                                        color: "#F08080",
                                        data: data[breakdownMode].data.map(
                                            (date, index) => {
                                                return {
                                                    name: date.date,
                                                    value: date.noAvailability,
                                                };
                                            },
                                        ),
                                    },
                                    {
                                        type: "bar",
                                        name: "Availability",
                                        symbol: "none",
                                        smooth: true,
                                        color: "#77DD77",
                                        stack: "current",
                                        data: data[breakdownMode].data.map(
                                            (date, index) => {
                                                return {
                                                    name: date.date,
                                                    value: date.availability,
                                                };
                                            },
                                        ),
                                    },
                                ],
                            }}
                        />
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No bookings or no availability data found for the selected period and filters." />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </Stack>
    );
};

const RevenueLossTableReport = ({ data, hotelID }) => {
    const [breakdownMode, setBreakdownMode] = useState(DEFAULT_BREAKDOWN_MODE);

    const columnDates = data?.columns?.[breakdownMode] || [];
    const rows = data?.rows || [];

    const hasData =
        columnDates.length > 0 &&
        rows.some(
            (row) =>
                row?.data?.[breakdownMode] &&
                Object.keys(row.data[breakdownMode]).length > 0,
        );

    return (
        <Stack direction={"column"} sx={{ pt: 2 }}>
            <Stack direction={"row"} alignItems="center" flex={1}>
                <Typography fontWeight={"bold"}>
                    Minimum revenue lost by rate plan and arrival date
                </Typography>
                {/* <InfoIcon /> */}
            </Stack>
            {hasData ? (
                <>
                    <Box component={HoverPaper}>
                        <TableContainer
                            sx={{
                                overflow: "auto",
                                maxHeight: "70em",
                            }}
                        >
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            style={{
                                                border: "1px solid lightgrey",
                                                fontWeight: "bold",
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 2,
                                                backgroundColor:
                                                    Colours.plainWhite,
                                                minWidth: "10em",
                                            }}
                                        >
                                            <BreakdownBySelector
                                                breakdownMode={breakdownMode}
                                                setBreakdownMode={
                                                    setBreakdownMode
                                                }
                                            />
                                        </TableCell>
                                        {columnDates.map((date) => (
                                            <TableCell
                                                key={date}
                                                colSpan={2}
                                                align="center"
                                                style={{
                                                    border: "1px solid lightgrey",
                                                    backgroundColor:
                                                        Colours.pillarBackgroundAttract,
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1,
                                                    minWidth: "15em",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight="bold"
                                                >
                                                    {breakdownMode ===
                                                    GROUP_DATE_MONTH
                                                        ? dayjs(date)
                                                              .format("MMM")
                                                              .toUpperCase()
                                                        : dayjs(date)
                                                              .format("MMM D")
                                                              .toUpperCase()}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                border: "1px solid lightgrey",
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 1,
                                                backgroundColor:
                                                    Colours.plainWhite,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                            >
                                                Rate Plan
                                            </Typography>
                                        </TableCell>
                                        {columnDates.map((date) => (
                                            <React.Fragment key={date}>
                                                <TableCell
                                                    style={{
                                                        border: "1px solid lightgrey",
                                                    }}
                                                >
                                                    <Typography variant="caption">
                                                        Reason
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        border: "1px solid lightgrey",
                                                    }}
                                                >
                                                    <Typography variant="caption">
                                                        Min rev
                                                    </Typography>
                                                </TableCell>
                                            </React.Fragment>
                                        ))}
                                    </TableRow>
                                    {rows.map(({ ratePlan, data }, i) => (
                                        <TableRow key={ratePlan}>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    border: "1px solid lightgrey",
                                                    backgroundColor:
                                                        i % 2 === 0
                                                            ? Colours.plainWhite
                                                            : Colours.backgroundWhite,
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1,
                                                }}
                                            >
                                                {ratePlan.trim()}
                                            </TableCell>
                                            {columnDates.map((date) => {
                                                const cell =
                                                    data?.[breakdownMode]?.[
                                                        date
                                                    ];
                                                const reason =
                                                    cell?.reason || "";
                                                const revenue =
                                                    cell?.revenue || null;
                                                return (
                                                    <React.Fragment key={date}>
                                                        <TableCell
                                                            style={{
                                                                border: "1px solid lightgrey",
                                                                backgroundColor:
                                                                    reason ===
                                                                    "Sold Out"
                                                                        ? Colours.notificationCard
                                                                        : reason ===
                                                                            "Closed Out"
                                                                          ? Colours.pillarBackgroundConvert
                                                                          : Colours.pillarBackgroundEngage,
                                                            }}
                                                        >
                                                            {reason}
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                border: "1px solid lightgrey",
                                                            }}
                                                        >
                                                            {revenue != null
                                                                ? fns(
                                                                      hotelID,
                                                                      revenue,
                                                                      {
                                                                          left: "currency",
                                                                      },
                                                                  )
                                                                : ""}
                                                        </TableCell>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            ) : (
                <Container
                    maxWidth={false}
                    component={HoverPaper}
                    sx={{ p: 2, backgroundColor: Colours.notificationCard }}
                >
                    <Table>
                        <TableBody>
                            <NoDataRow text="No unavailable rate plans or revenue loss data for the selected criteria." />
                        </TableBody>
                    </Table>
                </Container>
            )}
        </Stack>
    );
};

const AvailabilityInsightsScreen = () => {
    const { id: hotelID = "default" } = useSelector((state) => state.hotelID);
    const js = useSelector((state) => state.globalReportFilters);

    //const [update, setUpdate] = useState(0);

    const dataSearches = useSelector((state) => state.dataSearches);
    const [dataFiltered, setDataFiltered] = useState([]);

    const [dataSessionsTrend, setDataSessionsTrend] = useState([]); // {GROUP_DATE_DAY: [....] by default}
    const [dataSearchesForUnavailableDays, setDataSearchesForUnavailableDays] =
        useState([]);
    const [
        dataAvailabilityVsNoAvailability,
        setDataAvailabilityVsNoAvailability,
    ] = useState([]);

    const [dataMinimumRevenueLost, setDataMinimumRevenueLost] = useState({});

    const handleFilters = (data) => {
        return (Array.isArray(data) ? data : []).filter((search) => {
            const searchDate = dayjs(search.searchDate);
            const fromDate = dayjs(search.fromDate);
            const toDate = dayjs(search.toDate);

            const isSearchInRange =
                searchDate.isSameOrAfter(dayjs(js.fromDay)) &&
                searchDate.isSameOrBefore(dayjs(js.toDay));

            const isArrivalInRange =
                fromDate.isSameOrAfter(dayjs(js.fromArrivalDay)) &&
                toDate.isSameOrBefore(dayjs(js.toArrivalDay));

            const offers = js?.filters?.Offers ?? [];

            const matchesOffer =
                offers.length === 0 ||
                (Array.isArray(search.searchDetails) &&
                    search.searchDetails.some((detail) =>
                        offers.includes(detail.ratePlan),
                    ));

            return isSearchInRange && isArrivalInRange && matchesOffer;
        });
    };

    const processDataSessionsTrend = () => {
        let groups = {
            [GROUP_DATE_DAY]: {},
            [GROUP_DATE_WEEK]: {},
            [GROUP_DATE_MONTH]: {},
        };

        // Match unique sessions to it's group
        dataFiltered.forEach((search) => {
            const searchDate = dayjs(search.searchDate);
            const date = searchDate.format("YYYY-MM-DD");
            const week = dayjs(startOfWeekDate(date)).format("YYYY-MM-DD"); // Returns the of start of the week date to which the given date belongs
            const month = searchDate.format("YYYY-MM");

            if (!groups[GROUP_DATE_DAY][date])
                groups[GROUP_DATE_DAY][date] = new Set();
            groups[GROUP_DATE_DAY][date].add(search.sessionID);

            if (!groups[GROUP_DATE_WEEK][week])
                groups[GROUP_DATE_WEEK][week] = new Set();
            groups[GROUP_DATE_WEEK][week].add(search.sessionID);

            if (!groups[GROUP_DATE_MONTH][month])
                groups[GROUP_DATE_MONTH][month] = new Set();
            groups[GROUP_DATE_MONTH][month].add(search.sessionID);
        });

        // Convert Sets to sorted arrays with its average {data: [ {date, sessions}, ....], average}
        const buildGroupData = (groupObj) => {
            const entries = Object.entries(groupObj).sort(([a], [b]) =>
                a.localeCompare(b),
            );
            const data = [];
            let total = 0;

            entries.forEach(([date, sessionSet]) => {
                const count = sessionSet.size;
                data.push({ date, sessions: count });
                total += count;
            });

            const average = entries.length > 0 ? total / entries.length : 0;

            return { data, average };
        };

        return {
            [GROUP_DATE_DAY]: buildGroupData(groups[GROUP_DATE_DAY]),
            [GROUP_DATE_WEEK]: buildGroupData(groups[GROUP_DATE_WEEK]),
            [GROUP_DATE_MONTH]: buildGroupData(groups[GROUP_DATE_MONTH]),
        };
    };

    const processDataNoAvailabilityTrend = () => {
        let groups = {
            [GROUP_DATE_DAY]: {},
            [GROUP_DATE_WEEK]: {},
            [GROUP_DATE_MONTH]: {},
        };

        dataFiltered.forEach((search) => {
            if (!search.wasBooking) {
                (search.searchDetails || []).forEach((detail) => {
                    const dateStr = detail.noAvailabilityDate;
                    if (!dateStr) return;

                    const noAvDate = dayjs(dateStr);
                    const date = noAvDate.format("YYYY-MM-DD");
                    const week = dayjs(startOfWeekDate(date)).format(
                        "YYYY-MM-DD",
                    );
                    const month = noAvDate.format("YYYY-MM");

                    // Día
                    if (!groups[GROUP_DATE_DAY][date])
                        groups[GROUP_DATE_DAY][date] = 0;
                    groups[GROUP_DATE_DAY][date]++;

                    // Week
                    if (!groups[GROUP_DATE_WEEK][week])
                        groups[GROUP_DATE_WEEK][week] = 0;
                    groups[GROUP_DATE_WEEK][week]++;

                    // Month
                    if (!groups[GROUP_DATE_MONTH][month])
                        groups[GROUP_DATE_MONTH][month] = 0;
                    groups[GROUP_DATE_MONTH][month]++;
                });
            }
        });

        const buildGroupData = (groupObj) => {
            const entries = Object.entries(groupObj).sort(([a], [b]) =>
                a.localeCompare(b),
            );
            const data = [];
            let total = 0;

            entries.forEach(([date, count]) => {
                data.push({ date, searches: count });
                total += count;
            });

            return { data, total };
        };

        return {
            [GROUP_DATE_DAY]: buildGroupData(groups[GROUP_DATE_DAY]),
            [GROUP_DATE_WEEK]: buildGroupData(groups[GROUP_DATE_WEEK]),
            [GROUP_DATE_MONTH]: buildGroupData(groups[GROUP_DATE_MONTH]),
        };
    };

    const processDataAvailabilityVsNoAvailability = () => {
        let groups = {
            [GROUP_DATE_DAY]: {},
            [GROUP_DATE_WEEK]: {},
            [GROUP_DATE_MONTH]: {},
        };

        dataFiltered.forEach((search) => {
            const fromDate = dayjs(search.fromDate); // CheckIn date
            const day = fromDate.format("YYYY-MM-DD");
            const week = dayjs(startOfWeekDate(day)).format("YYYY-MM-DD");
            const month = fromDate.format("YYYY-MM");

            const setGroup = (groupKey, dateKey) => {
                if (!groups[groupKey][dateKey]) {
                    groups[groupKey][dateKey] = {
                        availability: 0,
                        noAvailability: 0,
                    };
                }

                const key = search.wasBooking
                    ? "availability"
                    : "noAvailability";
                groups[groupKey][dateKey][key]++;
            };

            setGroup(GROUP_DATE_DAY, day);
            setGroup(GROUP_DATE_WEEK, week);
            setGroup(GROUP_DATE_MONTH, month);
        });

        const buildGroupData = (groupObj) => {
            const entries = Object.entries(groupObj).sort(([a], [b]) =>
                a.localeCompare(b),
            );

            const data = [];
            let total = 0;

            entries.forEach(([date, { availability, noAvailability }]) => {
                data.push({ date, availability, noAvailability });
                total += availability + noAvailability;
            });

            return { data, total };
        };

        return {
            [GROUP_DATE_DAY]: buildGroupData(groups[GROUP_DATE_DAY]),
            [GROUP_DATE_WEEK]: buildGroupData(groups[GROUP_DATE_WEEK]),
            [GROUP_DATE_MONTH]: buildGroupData(groups[GROUP_DATE_MONTH]),
        };
    };

    const processDataMinimumRevenueLost = () => {
        const groups = {
            [GROUP_DATE_DAY]: {},
            [GROUP_DATE_WEEK]: {},
            [GROUP_DATE_MONTH]: {},
        };

        const columnSets = {
            [GROUP_DATE_DAY]: new Set(),
            [GROUP_DATE_WEEK]: new Set(),
            [GROUP_DATE_MONTH]: new Set(),
        };

        const ratePlansMap = new Map();

        // 1. Retrieve unique users
        const uniqueSessions = new Map();

        dataFiltered.forEach((search) => {
            if (!search.wasBooking && Array.isArray(search.searchDetails)) {
                const sessionID = search.sessionID;

                search.searchDetails.forEach((detail) => {
                    const dateStr = detail.noAvailabilityDate;
                    if (!dateStr) return;

                    const noAvDate = dayjs(dateStr);
                    const date = noAvDate.format("YYYY-MM-DD");
                    const ratePlan = detail.ratePlan;

                    const reason =
                        detail.reason?.trim() === "No Availability"
                            ? "Sold Out"
                            : detail.reason?.trim();
                    const revenue = Number(detail.revenue) || 0;

                    const current = uniqueSessions.get(sessionID);

                    if (!current || revenue < current.revenue) {
                        uniqueSessions.set(sessionID, {
                            date: dateStr,
                            revenue,
                            reason,
                            ratePlan,
                        });
                    }
                });
            }
        });
        const filterRateplan = js?.filters?.Offers ?? [];
        // 2. Accumulate minimum revenue per reason and date
        uniqueSessions.forEach(({ date, revenue, reason, ratePlan }) => {
            const noAvDate = dayjs(date);
            const day = noAvDate.format("YYYY-MM-DD");
            const week = dayjs(startOfWeekDate(noAvDate)).format("YYYY-MM-DD");
            const month = noAvDate.format("YYYY-MM");

            const groupDates = {
                [GROUP_DATE_DAY]: day,
                [GROUP_DATE_WEEK]: week,
                [GROUP_DATE_MONTH]: month,
            };

            for (const groupDate in groupDates) {
                const label = groupDates[groupDate];
                columnSets[groupDate].add(label);

                if (!groups[groupDate][label]) {
                    groups[groupDate][label] = 0;
                }

                if (
                    !ratePlansMap.has(ratePlan) &&
                    (filterRateplan.includes(ratePlan) ||
                        filterRateplan.length === 0)
                ) {
                    ratePlansMap.set(ratePlan, {
                        ratePlan,
                        data: {
                            [GROUP_DATE_DAY]: {},
                            [GROUP_DATE_WEEK]: {},
                            [GROUP_DATE_MONTH]: {},
                        },
                    });
                }

                const ratePlanEntry = ratePlansMap.get(ratePlan);

                if (ratePlanEntry && ratePlanEntry.data) {
                    const ratePlanData = ratePlanEntry.data;

                    if (!ratePlanData[groupDate][label]) {
                        ratePlanData[groupDate][label] = {
                            reason,
                            revenue,
                        };
                    } else {
                        ratePlanData[groupDate][label].revenue += revenue;
                        // mantenemos la razón original
                    }
                }
            }
        });

        const sortSet = (set) =>
            Array.from(set).sort((a, b) => a.localeCompare(b));

        const columns = {
            [GROUP_DATE_DAY]: sortSet(columnSets[GROUP_DATE_DAY]),
            [GROUP_DATE_WEEK]: sortSet(columnSets[GROUP_DATE_WEEK]),
            [GROUP_DATE_MONTH]: sortSet(columnSets[GROUP_DATE_MONTH]),
        };

        const rows = Array.from(ratePlansMap.values());

        return {
            rows,
            columns,
        };
    };

    useEffect(() => {
        // setUpdate(update + 1);
        let auxDataFiltered = handleFilters(dataSearches.data ?? []);
        setDataFiltered(auxDataFiltered);
    }, [js, dataSearches]);

    useEffect(() => {
        let auxDataSessionsTrend = processDataSessionsTrend();
        setDataSessionsTrend(auxDataSessionsTrend);

        let auxDataSearchesForUnavailableDays =
            processDataNoAvailabilityTrend();
        setDataSearchesForUnavailableDays(auxDataSearchesForUnavailableDays);

        let auxDataAvailabilityVsNoAvailability =
            processDataAvailabilityVsNoAvailability();
        setDataAvailabilityVsNoAvailability(
            auxDataAvailabilityVsNoAvailability,
        );

        let auxDataMinimumRevenueLost = processDataMinimumRevenueLost();
        setDataMinimumRevenueLost(auxDataMinimumRevenueLost);
    }, [dataFiltered]);

    return (
        <>
            <DataLoader Searches />
            <Container maxWidth={false}>
                <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
                    Availability insights dashboard
                </Typography>
                <Typography variant="subtitle1" color="initial">
                    Track booking engine activity, identify dates with no
                    availability, and uncover missed revenue opportunities by
                    rate plan and arrival date.
                </Typography>

                {/**Old -> AroInsightsScreen - Go there for more info */}
                <br />
                <FiltersReport
                    hideExports
                    otherProps={{
                        // DATATYPE_TITLE: "Show insights by",
                        DATATYPE_VALUES: {
                            [DATATYPE_SEARCHES]: "Search date",
                            [DATATYPE_CHECKIN]: "Arrival date",
                        },
                        TIME_RANGE: {
                            fromDateProps: {
                                value: dayjs(js.fromDay),
                                maxDate: dayjs(),
                            },
                            toDateProps: {
                                value: dayjs(js.toDay),
                                maxDate: dayjs(),
                            },
                        },
                        TIME_ARRIVAL_RANGE: {
                            fromDateProps: {
                                value: dayjs(js.fromArrivalDay),
                            },
                            toDateProps: {
                                value: dayjs(js.toArrivalDay),
                            },
                        },
                    }}
                    defaultValues={{
                        presetDate: PRESET_MONTH_TO_DATE,
                        presetArrivalDate: PRESET_NEXT_3_MONTHS,
                        dataType: DATATYPE_SEARCHES,
                        filters: {
                            Cancellations: "included",
                            Comparison: COMPARISON_SAME_TIME_LAST_YEAR,
                            Target: TARGET_OFF,
                            filters: {},
                        },
                    }}
                    BookingPresetOptions={[
                        PRESET_MONTH_TO_DATE,
                        PRESET_YEAR_TO_DATE,
                        PRESET_LAST_FULL_MONTH,
                        PRESET_LAST_YEAR,
                        PRESET_CHOOSE_MONTH,
                        PRESET_YESTERDAY,
                        PRESET_LAST_7_DAYS,
                        PRESET_LAST_30_DAYS,
                        PRESET_LAST_3_MONTHS,
                        PRESET_LAST_6_MONTHS,
                        PRESET_LAST_12_MONTHS,
                    ]}
                    ArrivalPresetOptions={[
                        PRESET_TOMORROW,
                        PRESET_NEXT_MONTH,
                        PRESET_NEXT_3_MONTHS,
                    ]}
                    hide={[
                        "DATATYPE",
                        "RESDATE_PERIOD",
                        "TIME_BOOKING_RANGE",
                        "CANCELLATIONS",
                        "COMPARISON",
                        "CUSTOM_FILTERS",
                        "FILTERS_VOUCHERS",
                        "FILTERS_TYPES",
                        "FILTERS_COUNTRIES",
                        "FILTERS_ROOMS",
                        "FILTERS_EXTRAS",
                        "FILTERS",
                    ]}
                    rename={{
                        TIME_PERIOD: "Search period",
                        TIME_RANGE: "Custom search date",
                    }}
                    setProcessed={() => {}}
                />
                <SessionsTrendReport
                    data={dataSessionsTrend}
                    hotelID={hotelID}
                />

                <SearchesForUnavailableDaysReport
                    data={dataSearchesForUnavailableDays}
                    hotelID={hotelID}
                />
                <AvailabilityVsNoAvailabilityReport
                    data={dataAvailabilityVsNoAvailability}
                    hotelID={hotelID}
                />

                <RevenueLossTableReport
                    data={dataMinimumRevenueLost}
                    hotelID={hotelID}
                />
            </Container>
        </>
    );
};
export default AvailabilityInsightsScreen;
